import React, { useMemo, useState, useEffect } from 'react';
import { Box, CircularProgress, Backdrop, Typography } from '@mui/material';
import ByzMuiSnackBar from 'src/components/core-components/mui/ByzMuiSnackBar';
import nurseService from 'src/api/nurse/nurseService';
import ByzMuiOpenProcessMenu from 'src/components/core-components/mui/ByzMuiOpenProcessMenu';
import OpenComponentDialog from 'src/components/shared-components/modal/openComponentModal';
import { useTranslate } from 'src/locales';
import ByzSelectListPopupInput from 'src/components/core-components/mui/inputs/ByzSelectListPopupInput';
import { useAppSelector } from 'src/app/hooks';
import MedicineDetail from './medicine-detail';
import ChangeApplyTime from './change-apply-time';
import dayjs from 'dayjs';
import CancelDialog from 'src/components/core-components/mui/CancalDialog';
import type { CardItemProps } from './card-item.types';

export const MedicineCard: React.FC<CardItemProps> = ({ data, patientId, upn, refetch }) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);
  const [detailOpen, setDetailOpen] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [changeTimeOpen, setChangeTimeOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState<any>(null);


  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    open: false,
    title: '',
    message: '',
    type: 'success',
  });

  const showSnackbar = (message: string, type: 'success' | 'error' = 'success', title?: string) => {
    setSnackbar({
      open: true,
      message,
      type,
      title: title || (type === 'success' ? t('success') : t('error')),
    });
  };

  const handleOpenDetail = () => {
    setDetailOpen(true);
  };

  // Sunucudan gelen doğrulama bilgisine göre başlangıç durumunu ayarla
  useEffect(() => {
    const od: any = data?.orderdetail;
    const isServerVerified =
      od?.isverified === true ||
      !!(od?.authenticator) ||
      !!(od?.authenticationtime);
    setVerified(isServerVerified);
  }, [data?.orderdetail]);

  const handleCloseDetail = () => {
    setDetailOpen(false);
  };

  const handleOpenChangeTime = (e?: any) => {
    e?.stopPropagation?.();
    setChangeTimeOpen(true);
  };

  const handleCloseChangeTime = () => {
    setChangeTimeOpen(false);
  };

  const handleSaveChangeTime = async (newTime: Date) => {
    const srumid = data?.orderdetail?.srumid;
    if (!srumid) {
      showSnackbar('Srum ID bulunamadı.', 'error');
      return;
    }

    // "Tamam" sonrası modal görünmesin; sadece loading overlay görünsün
    handleCloseChangeTime();
    setLoading(true);
    try {
      const response = await nurseService.order.orderAppDateChange<{
        success?: boolean;
        issuccess?: boolean;
        message?: string;
        data?: any;
      }>({
        srumid: Number(srumid),
        appdate: dayjs(newTime).toISOString()
      });

      const isSuccess =
        (response as any)?.success === true ||
        (response as any)?.issuccess === true ||
        typeof (response as any)?.data === 'number';

      if (isSuccess) {
        showSnackbar((response as any)?.message || 'Uygulama saati başarıyla değiştirildi.', 'success');
        handleCloseChangeTime();
        refetch?.();
      } else {
        showSnackbar((response as any)?.message || 'İşlem başarısız.', 'error');
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.response?.data?.error || 'Bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
    }
  };



  const handleApplyOrder = async (e?: any) => {
    e?.stopPropagation?.();
    const warningId = (e as any)?.warningId as number | undefined;
    const srumid = data?.orderdetail?.srumid;
    const ptid = patientId || (data?.orderdetail as any)?.ptid;

    if (!srumid) {
      showSnackbar('Order ID bilgisi bulunamadı.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await nurseService.order.applyOrder<{ success?: boolean; issuccess?: boolean; message?: string; data?: any }>({
        upn: Number(upn) || 0,
        ptid: Number(ptid) || Number((data?.orderdetail as any)?.ptid) || 0,
        srumid: Number(srumid),
        warningId: warningId ?? null,
        druginfos: [
          {
            srumid: Number(srumid),
            idtdid: (data?.orderdetail as any)?.idtdid || 0,
            ppid: (data?.orderdetail as any)?.ppid || 0,
          }
        ]
      });

      const isSuccess =
        (response as any)?.success === true ||
        (response as any)?.issuccess === true ||
        typeof (response as any)?.data === 'number';
      const serverMessage = (response as any)?.message;

      if (isSuccess) {
        showSnackbar(serverMessage || 'Order başarıyla uygulandı.', 'success');
        refetch?.();
      } else {
        showSnackbar(serverMessage || 'İşlem başarısız.', 'error');
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.response?.data?.error || 'Bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNotApplicable = async (note: string = '') => {
    const srumid = data?.orderdetail?.srumid;
    const ptid = patientId || (data?.orderdetail as any)?.ptid;

    if (!srumid) {
      showSnackbar('Order ID bulunamadı.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await nurseService.order.notApplicable<{ success: boolean; message: string }>({
        srumid: Number(srumid),
        ptid: Number(ptid) || Number((data?.orderdetail as any)?.ptid) || 0,
        warningpdid: 0,
        note: note || 'Uygulanamadı olarak işaretlendi'
      });

      if ((response as any)?.success) {
        showSnackbar((response as any)?.message || 'İşlem başarıyla kaydedildi.', 'success');
        refetch?.();
      } else {
        showSnackbar((response as any)?.message || 'İşlem başarısız.', 'error');
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.response?.data?.error || 'Bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrderTermination = () => {
    if (loading) return;
    setCancelDialogOpen(true);
  };

  const handleUndoApplyOrder = async () => {
    const srumid = data?.orderdetail?.srumid;
    if (!srumid) {
      showSnackbar('Order ID bilgisi bulunamadı.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await nurseService.order.undoApplyOrder<{ success?: boolean; message?: string; data?: any }>({
        srumid: Number(srumid),
      });

      const isSuccess = (response as any)?.success === true || typeof (response as any)?.data === 'number';
      const serverMessage = (response as any)?.message;

      if (isSuccess) {
        showSnackbar(serverMessage || 'Uygulama başarıyla geri alındı.', 'success');
        refetch?.();
      } else {
        showSnackbar(serverMessage || 'İşlem başarısız.', 'error');
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.response?.data?.error || 'Bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyWithWarning = () => {
    if (loading) return;
    setSelectedWarning(null);
    setWarningDialogOpen(true);
  };

  const effectiveOffId = useMemo(() => {
    const first = (user as any)?.UserAssPriviliges?.[0];
    return Number(first?.OFF_ID || 0);
  }, [user]);

  const warningListUrl = useMemo(() => {
    if (!effectiveOffId) return undefined;
    return `/nurse/api/Order/GetApplyWarningList?offId=${effectiveOffId}`;
  }, [effectiveOffId]);

  const warningColumns = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 100 },
      { field: 'name', headerName: t('name') || 'Adı', flex: 1 },
    ],
    [t]
  );

  const handleConfirmCancelOrderTermination = async (reason: string) => {
    const srumId = data?.orderdetail?.srumid;

    if (!srumId) {
      showSnackbar('Order ID bulunamadı.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await nurseService.order.cancelOrderTermination<{ success: boolean; message: string }>({
        srumId: Number(srumId),
        message: reason?.trim() ? reason.trim() : null,
      });

      if ((response as any)?.success) {
        showSnackbar((response as any)?.message || 'Sonlandırma başarıyla iptal edildi.', 'success');
        refetch?.();
      } else {
        showSnackbar((response as any)?.message || 'İşlem başarısız.', 'error');
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.response?.data?.error || 'Bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
      setCancelDialogOpen(false);
    }
  };

  const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const handleVerifyOrder = async (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const srumId = data?.orderdetail?.srumid;
    if (!srumId) {
      showSnackbar('Order bilgisi bulunamadı.', 'error');
      return;
    }
    setVerifyLoading(true);
    try {
      const response = await nurseService.order.verifyOrder<{ success?: boolean; message?: string; data?: any }>({
        srumid: srumId,
        resultnote: 'Order doğrulandı',
      });
      const isSuccess = (response as any)?.success === true || typeof (response as any)?.data === 'number';
      const serverMessage = (response as any)?.message;

      if (isSuccess) {
        showSnackbar(serverMessage || 'Order başarıyla doğrulandı.', 'success');
        setVerified(true);
        refetch?.();
      } else {
        showSnackbar(serverMessage || 'Doğrulama başarısız.', 'error');
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Doğrulama sırasında bir hata oluştu.', 'error');
    } finally {
      setVerifyLoading(false);
    }
  };

  const cardClickHandler =
    detailOpen || changeTimeOpen || loading || cancelDialogOpen || snackbar.open || warningDialogOpen
      ? undefined
      : handleOpenDetail;

  return (
    <Box
      sx={{
        position: 'relative',
        boxShadow: '0px 1px 2px #e0e5eb, 0px 0px 0px 1px #e0e5eb',
        borderRadius: '8px',
        backgroundColor: '#fff',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0px 0px 10px',
        boxSizing: 'border-box',
        gap: '4px',
        textAlign: 'left',
        fontSize: '12px',
        color: '#767a85',
        fontFamily: 'Poppins',
        cursor: cardClickHandler ? 'pointer' : 'default',
      }}
      onClick={cardClickHandler}
    >
      <Box
        sx={{
          width: '286px',
          height: '4px',
          position: 'relative',
          backgroundColor: '#2979c3',
        }}
      />
      <Box
        sx={{
          width: '270px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '5px',
        }}
      >
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            gap: '68px',
            flexShrink: 0,
            color: '#2f353d',
          }}
        >
          <Box
            sx={{
              width: '140px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                alignSelf: 'stretch',
                position: 'relative',
                letterSpacing: '-0.01em',
                lineHeight: '140%',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {(data?.orderdetail?.reqmatname?.trim() || data?.orderdetail?.matname?.trim() || 'İlaç Adı')}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}
            onClick={stopPropagation}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: '1px solid #2196F3',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.6,
                pointerEvents: 'none',
              }}
            >
              <img
                src="/assets/icons/print/print.svg"
                alt="Print"
                style={{ width: '16px', height: '16px' }}
              />
            </Box>
            {!loading && (
              <ByzMuiOpenProcessMenu
                menuItems={(() => {
                  const isApplied = data?.orderdetail?.state === 1;
                  return [
                    {
                      label: t('nurse.order-medicine.menu-items.cannot-apply'),
                      onClick: () => handleNotApplicable(),
                      disabled: isApplied,
                    },
                    {
                      label: t('nurse.order-medicine.menu-items.applied-with-warning'),
                    onClick: handleApplyWithWarning,
                    disabled: isApplied,
                    },
                    {
                      label: t('nurse.order-medicine.menu-items.undo-application'),
                      onClick: handleUndoApplyOrder,
                      divider: true,
                      disabled: !isApplied,
                    },
                    {
                      label: t('nurse.order-medicine.menu-items.terminate-cancel'),
                      onClick: handleCancelOrderTermination,
                      divider: true,
                      disabled: isApplied,
                    },
                    {
                      label: t('nurse.order-medicine.menu-items.vademecum-drug-info'),
                      onClick: () => { },
                      disabled: true,
                    },
                    {
                      label: t('nurse.order-medicine.menu-items.interaction'),
                      onClick: () => { },
                      divider: true,
                      disabled: true,
                    },
                    {
                      label: t('nurse.order-medicine.menu-items.treatment-planning'),
                      onClick: () => { },
                      divider: true,
                      disabled: true,
                    },
                    {
                      label: t('nurse.order-medicine.menu-items.change-application-time'),
                      onClick: handleOpenChangeTime,
                    },
                  ];
                })()}
                hideHeaderTitle
                sx={{ p: 0 }}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: '271px',
            height: '1px',
            position: 'relative',
            borderTop: '1px solid #e6e6e6',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            width: '272px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexShrink: 0,
            fontSize: '13px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '29px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: '2px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      letterSpacing: '-0.01em',
                      lineHeight: '22px',
                      fontWeight: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('nurse.order-medicine.card-fields.pharmaceutical')}
                  </Box>
                  <Box
                    sx={{
                      position: 'relative',
                      fontSize: '12px',
                      letterSpacing: '-0.01em',
                      lineHeight: '22px',
                      fontWeight: 500,
                      color: '#1f2026',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {(data?.orderdetail?.drugformtypename?.trim() && data.orderdetail.drugformtypename.trim() !== '')
                      ? data.orderdetail.drugformtypename.trim()
                      : '-'}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  width: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.dose')}
                </Box>
                <Box
                  sx={{
                    alignSelf: 'stretch',
                    position: 'relative',
                    fontSize: '12px',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 500,
                    color: '#1f2026',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(data?.orderdetail?.dose?.trim() && data.orderdetail.dose.trim() !== '')
                    ? data.orderdetail.dose.trim()
                    : '-'}
                </Box>
              </Box>
              <Box
                sx={{
                  width: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.application-time')}
                </Box>
                <Box
                  sx={{
                    alignSelf: 'stretch',
                    position: 'relative',
                    fontSize: '12px',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 500,
                    color: '#1f2026',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {data?.orderdetail?.planhour
                    ? dayjs(data.orderdetail.planhour).format('HH:mm')
                    : '-'}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: '271px',
            height: '1px',
            position: 'relative',
            borderTop: '1px solid #e6e6e6',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            width: '272px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexShrink: 0,
            fontSize: '13px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '29px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: '2px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      letterSpacing: '-0.01em',
                      lineHeight: '22px',
                      fontWeight: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('nurse.order-medicine.card-fields.application-route')}
                  </Box>
                  <Box
                    sx={{
                      position: 'relative',
                      fontSize: '12px',
                      letterSpacing: '-0.01em',
                      lineHeight: '22px',
                      fontWeight: 500,
                      color: '#1f2026',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {(data?.orderdetail?.dumname?.trim() && data.orderdetail.dumname.trim() !== '')
                      ? data.orderdetail.dumname.trim()
                      : '-'}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  width: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.application-duration')}
                </Box>
                <Box
                  sx={{
                    alignSelf: 'stretch',
                    position: 'relative',
                    fontSize: '12px',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 500,
                    color: '#1f2026',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(data?.orderdetail?.druginfusioninfo?.trim() && data.orderdetail.druginfusioninfo.trim() !== '')
                    ? data.orderdetail.druginfusioninfo.trim()
                    : '-'}
                </Box>
              </Box>
              <Box
                sx={{
                  width: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.unit')}
                </Box>
                <Box
                  sx={{
                    alignSelf: 'stretch',
                    position: 'relative',
                    fontSize: '12px',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 500,
                    color: '#1f2026',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(data?.orderdetail?.appunitname?.trim() && data.orderdetail.appunitname.trim() !== '')
                    ? data.orderdetail.appunitname.trim()
                    : (data?.orderdetail?.dosetypename?.trim() && data.orderdetail.dosetypename.trim() !== '')
                      ? data.orderdetail.dosetypename.trim()
                      : '-'}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '5px',
            flexShrink: 0,
            color: '#2979c3',
          }}
        >
          {(() => {
            const isApplied = data?.orderdetail?.state === 1;
            return (
              <Box
                sx={{
                  height: '27px',
                  width: '132.5px',
                  borderRadius: '100px',
                  backgroundColor: isApplied ? 'rgba(3, 202, 26, 0.1)' : 'rgba(41, 121, 195, 0.1)',
                  border: isApplied ? '1px solid #03ca1a' : '1px solid #2979c3',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2px 4px',
                  color: isApplied ? '#03ca1a' : '#2979c3',
                  opacity: loading || isApplied ? 0.6 : 1,
                  pointerEvents: loading || isApplied ? 'none' : 'auto',
                }}
                onClick={handleApplyOrder}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.02em',
                    lineHeight: '15px',
                  }}
                >
                  {isApplied ? 'Uygulandı' : t('nurse.order-medicine.buttons.apply')}
                </Box>
              </Box>
            );
          })()}
          <Box
            sx={{
              height: '27px',
              width: '132.5px',
              borderRadius: '100px',
              backgroundColor: verified ? 'rgba(76, 175, 80, 0.1)' : 'rgba(41, 121, 195, 0.1)',
              border: verified ? '1px solid #4caf50' : '1px solid #2979c3',
              boxSizing: 'border-box',
              overflow: 'hidden',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2px 4px',
              cursor: verified || verifyLoading ? 'default' : 'pointer',
              opacity: verified || verifyLoading ? 0.6 : 1,
              pointerEvents: verified || verifyLoading ? 'none' : 'auto',
            }}
            onClick={handleVerifyOrder}
          >
            {verifyLoading ? (
              <CircularProgress size={14} sx={{ color: '#2979c3' }} />
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  letterSpacing: '-0.02em',
                  lineHeight: '15px',
                  color: verified ? '#4caf50' : '#2979c3',
                }}
              >
                {verified ? 'Doğrulandı' : t('nurse.order-medicine.buttons.verify')}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <OpenComponentDialog
        open={detailOpen}
        onClose={handleCloseDetail}
        title={t('nurse.order-medicine.detail.medicine-detail')}
        showPatientData={false}
        maxWidth="xl"
        fullWidth
      >
        <MedicineDetail onClose={handleCloseDetail} patientId={patientId} srumId={data?.orderdetail?.srumid} />
      </OpenComponentDialog>

      <ByzMuiSnackBar
        open={snackbar.open}
        title={snackbar.title}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />

      <CancelDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancelOrderTermination}
        title={t("warning-Message")}
        description={t("nurse.order-medicine.dialogs.cancel-request.description")}
        placeholder={t("description-required")}
        cancelText={t("no")}
        confirmText={t("yes")}
        requireInput={true}
        loading={loading}
        width="650px"
        height="420px"
      />

      {/* Uygulandı (Uyarı Var!) seçimi */}
      <ByzSelectListPopupInput
        dialogOpen={warningDialogOpen}
        setDialogOpen={setWarningDialogOpen}
        url={warningListUrl}
        method="GET"
        dialogTitle={t('nurse.order-medicine.menu-items.applied-with-warning') || 'Uygulandı (Uyarı Var!)'}
        dialogShowOkButton={true}
        singleSelection={true}
        displayField="name"
        columns={warningColumns}
        value={selectedWarning?.name || ''}
        selectedItem={selectedWarning}
        selectedId={selectedWarning?.id ? String(selectedWarning.id) : undefined}
        onChange={(selected) => {
          const item = Array.isArray(selected) ? selected[0] : selected;
          if (!item) {
            showSnackbar(t('warning-Message') || 'Lütfen bir uyarı seçiniz.', 'error');
            return;
          }
          const wid = Number((item as any)?.id);
          if (!wid) {
            showSnackbar(t('warning-Message') || 'Lütfen bir uyarı seçiniz.', 'error');
            return;
          }
          setSelectedWarning(item);
          handleApplyOrder({ warningId: wid });
        }}
        fullWidth
        sx={{ display: 'none' }}
      />

      <OpenComponentDialog
        open={changeTimeOpen}
        onClose={handleCloseChangeTime}
        title={t('nurse.order-medicine.dialogs.change-apply-time.title')}
        showPatientData={false}
        maxWidth="xs"
      >
        <ChangeApplyTime
          initialTime={data?.orderdetail?.planhour}
          onClose={handleCloseChangeTime}
          onSave={handleSaveChangeTime}
        />
      </OpenComponentDialog>

      {/* Uygulama / iade / iptal işlemleri sırasında ekran üstü loading */}
      <Backdrop
        open={loading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>
          {t('loading')}
        </Typography>
      </Backdrop>

      {/* Uygulama / iade / iptal işlemleri sırasında ekran üstü loading */}
      <Backdrop
        open={loading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>
          {t('loading')}
        </Typography>
      </Backdrop>
    </Box>
  );
};

